import { useAuth } from '../lib/auth';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Calendar, Heart, Sun, Flame, BookOpen, Armchair } from 'lucide-react';

interface TodayData {
  hebrewDate: {
    day: number;
    month: number;
    monthName: string;
    monthHebrew: string;
    year: number;
    dayOfWeek: number;
    dayOfWeekName: string;
    hebrewFormat: string;
  };
  holidays: string[];
  parsha: string | null;
  omer: number | null;
  isMajorHoliday: boolean;
  isMinorHoliday: boolean;
  isFastDay: boolean;
  isRoshChodesh: boolean;
  candleLighting: string | null;
  havdalah: string | null;
  zmanim: {
    dawn: string;
    sunrise: string;
    latestShema: string;
    latestShacharit: string;
    midday: string;
    earliestMincha: string;
    afternoon: string;
    plagHaMincha: string;
    sunset: string;
    nightfall: string;
  };
}

interface UpcomingYahrzeit {
  date: string;
  hebrewDate: {
    day: number;
    monthName: string;
  };
  deceasedName: string;
  deceasedHebrewName: string | null;
  relationship: string;
  daysUntil: number;
}

export function DashboardPage() {
  const { user } = useAuth();

  const { data: todayData } = useQuery({
    queryKey: ['today-calendar'],
    queryFn: async () => {
      const response = await api.get<TodayData>('/calendar/today');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: upcomingYahrzeits } = useQuery({
    queryKey: ['upcoming-yahrzeits'],
    queryFn: async () => {
      const response = await api.get<UpcomingYahrzeit[]>('/calendar/upcoming-yahrzeits?days=14');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: eventsCount } = useQuery({
    queryKey: ['upcoming-events-count'],
    queryFn: async () => {
      const from = new Date().toISOString();
      const response = await api.get<unknown[]>(`/events?includePrivate=true&from=${from}&limit=1`);
      if (!response.success) return 0;
      return response.meta?.total ?? 0;
    },
  });

  const { data: honorsData } = useQuery({
    queryKey: ['my-honors'],
    queryFn: async () => {
      const response = await api.get<{ upcoming: unknown[]; past: unknown[] }>('/gabbai/my');
      if (!response.success) return { upcoming: [], past: [] };
      return response.data ?? { upcoming: [], past: [] };
    },
  });

  const { data: seatsData } = useQuery({
    queryKey: ['my-seats'],
    queryFn: async () => {
      const response = await api.get<unknown[]>('/seating/my-seats');
      if (!response.success) return [];
      return response.data ?? [];
    },
  });

  const displayName = user?.member
    ? `${user.member.firstName} ${user.member.lastName}`
    : user?.email;

  return (
    <div className="space-y-8">
      {/* Welcome Section with Hebrew Date */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Welcome, {user?.member?.firstName || 'Member'}!</h1>
            <p className="mt-1 text-primary-100">
              {user?.organization.name} Member Portal
            </p>
          </div>
          {todayData && (
            <div className="text-right">
              <p className="text-xl font-semibold">{todayData.hebrewDate.hebrewFormat}</p>
              <p className="text-sm text-primary-200">
                {todayData.hebrewDate.day} {todayData.hebrewDate.monthName} {todayData.hebrewDate.year}
              </p>
              {todayData.parsha && (
                <p className="text-sm text-primary-200 mt-1">Parashat {todayData.parsha}</p>
              )}
            </div>
          )}
        </div>
        {todayData && todayData.holidays.length > 0 && (
          <div className="mt-4 pt-4 border-t border-primary-500">
            <p className="text-sm text-primary-200">
              {todayData.holidays.join(' • ')}
            </p>
          </div>
        )}
      </div>

      {/* Today's Info Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Zmanim Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sun className="h-5 w-5 text-amber-500" />
            Today's Zmanim
          </h2>
          {todayData ? (
            <div className="space-y-3">
              <ZmanimRow label="Sunrise" time={todayData.zmanim.sunrise} />
              <ZmanimRow label="Latest Shema" time={todayData.zmanim.latestShema} highlight />
              <ZmanimRow label="Latest Shacharit" time={todayData.zmanim.latestShacharit} />
              <ZmanimRow label="Midday" time={todayData.zmanim.midday} />
              <ZmanimRow label="Mincha Gedolah" time={todayData.zmanim.earliestMincha} />
              <ZmanimRow label="Plag HaMincha" time={todayData.zmanim.plagHaMincha} />
              <ZmanimRow label="Sunset" time={todayData.zmanim.sunset} highlight />
              <ZmanimRow label="Nightfall" time={todayData.zmanim.nightfall} />
              {todayData.candleLighting && (
                <div className="pt-3 border-t">
                  <ZmanimRow label="Candle Lighting" time={todayData.candleLighting} highlight />
                </div>
              )}
              {todayData.havdalah && (
                <ZmanimRow label="Havdalah" time={todayData.havdalah} highlight />
              )}
            </div>
          ) : (
            <div className="animate-pulse space-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-6 bg-gray-100 rounded" />
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Yahrzeits */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Flame className="h-5 w-5 text-amber-500" />
            Upcoming Yahrzeits
          </h2>
          {upcomingYahrzeits && upcomingYahrzeits.length > 0 ? (
            <div className="space-y-3">
              {upcomingYahrzeits.slice(0, 5).map((y, i) => (
                <div key={i} className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="font-medium text-gray-900">{y.deceasedName}</p>
                  {y.deceasedHebrewName && (
                    <p className="text-sm text-gray-600">{y.deceasedHebrewName}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {y.relationship} • {y.hebrewDate.day} {y.hebrewDate.monthName}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    {y.daysUntil === 0 ? 'Today' : y.daysUntil === 1 ? 'Tomorrow' : `In ${y.daysUntil} days`}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No upcoming yahrzeits in the next 14 days</p>
          )}
          <a href="#yahrzeits" className="mt-4 block text-sm font-medium text-primary-600 hover:text-primary-500">
            Manage Yahrzeits →
          </a>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h2>
          <div className="space-y-3">
            <QuickLinkItem label="Update My Profile" href="#profile" />
            <QuickLinkItem label="View Member Directory" href="#directory" />
            <QuickLinkItem label="Make a Donation" href="#donate" />
            <QuickLinkItem label="View Events" href="#events" />
            <QuickLinkItem label="Manage Yahrzeits" href="#yahrzeits" />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickStatCard
          icon={Calendar}
          label="Upcoming Events"
          value={String(eventsCount ?? 0)}
          subtext="Coming up"
          color="blue"
        />
        <QuickStatCard
          icon={Heart}
          label="Yahrzeits"
          value={String(upcomingYahrzeits?.length || 0)}
          subtext="Next 14 days"
          color="purple"
        />
        <QuickStatCard
          icon={BookOpen}
          label="Honors"
          value={String(honorsData?.upcoming?.length ?? 0)}
          subtext="Upcoming"
          color="green"
        />
        <QuickStatCard
          icon={Armchair}
          label="Assigned Seats"
          value={String(seatsData?.length ?? 0)}
          subtext="High Holidays"
          color="orange"
        />
      </div>

      {/* Account Info Footer */}
      <div className="text-sm text-gray-500 border-t border-gray-200 pt-4">
        Logged in as <span className="font-medium text-gray-700">{displayName}</span>
      </div>
    </div>
  );
}

function ZmanimRow({ label, time, highlight }: { label: string; time: string; highlight?: boolean }) {
  return (
    <div className={`flex justify-between items-center py-1 ${highlight ? 'font-medium' : ''}`}>
      <span className={highlight ? 'text-gray-900' : 'text-gray-600'}>{label}</span>
      <span className={highlight ? 'text-primary-600' : 'text-gray-900'}>{time}</span>
    </div>
  );
}

function QuickStatCard({
  icon: Icon,
  label,
  value,
  subtext,
  color,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
  subtext: string;
  color: 'blue' | 'purple' | 'green' | 'orange';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2 ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
      <p className="mt-2 text-xs text-gray-400">{subtext}</p>
    </div>
  );
}

function QuickLinkItem({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
    >
      <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
      <span className="text-gray-400 group-hover:text-gray-600">→</span>
    </a>
  );
}
